import React, { forwardRef } from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ 
    label, 
    error, 
    helperText, 
    leftIcon, 
    rightIcon, 
    className = '',
    fullWidth = false,
    ...props 
  }, ref) => {
    const inputWrapperClasses = `
      relative flex items-center rounded-md border 
              ${error ? 'border-error-500' : 'border-primary-200'} 
      ${error ? 'focus-within:border-error-500' : 'focus-within:border-primary-500'} 
      bg-white transition-colors
      ${fullWidth ? 'w-full' : ''}
    `;
    
    const inputClasses = `
      block h-10 w-full rounded-lg py-2 text-primary-900 placeholder:text-primary-400
      focus:outline-none
      ${leftIcon ? 'pl-10' : 'pl-3'}
      ${rightIcon ? 'pr-10' : 'pr-3'}
    `;
    
    return (
      <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
        {label && (
          <label className="mb-1.5 block text-sm font-semibold text-primary-700">
            {label}
          </label>
        )}
        
        <div className={inputWrapperClasses}>
          {leftIcon && (
            <div className="absolute left-3 flex h-full items-center text-primary-400">
              {leftIcon}
            </div>
          )}
          
          <input 
            ref={ref}
            className={inputClasses}
            {...props} 
          />
          
          {rightIcon && (
            <div className="absolute right-3 flex h-full items-center text-primary-400">
              {rightIcon}
            </div>
          )}
        </div>
        
        {(error || helperText) && (
          <p className={`mt-1 text-sm ${error ? 'text-error-500' : 'text-primary-500'}`}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;